import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Nav } from "../components/Nav";
import { useAuth } from "../AuthContext";
import { useLang } from "../i18n";
import { AREA_META } from "../constants/areaMeta";
import { api } from "../api";
import { BioAge } from "../components/BioAge";
import { RiseMoment } from "../components/RiseMoment";
import { ActivityRings } from "../components/ActivityRings";
import { WorkoutCard } from "../components/WorkoutCard";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { ArrowRight, NotebookPen, BarChart3, Bell, X, Target, ChevronDown, Dumbbell, Gem } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [program, setProgram] = useState(null);
  const [goals, setGoals] = useState([]);
  const [openGoal, setOpenGoal] = useState(null);
  const [reminderDismissed, setReminderDismissed] = useState(
    () => localStorage.getItem("rise_reminder_day") === new Date().toISOString().slice(0, 10)
  );

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/program").then((r) => setProgram(r.data)).catch(() => {});
    api.get(`/goals?lang=${lang}`).then((r) => setGoals(r.data.goals || [])).catch(() => {});
  }, [lang]);

  const analysisDay = user?.start_date
    ? Math.min(Math.floor((Date.now() - new Date(user.start_date)) / 86400000) + 1, 7)
    : 1;
  const journeyActive = program && Array.isArray(program.weeks) && program.weeks.length > 0;
  const analysisComplete = program && (program.analysis_complete || (program.day && program.day >= 8));

  const todayISO = new Date().toISOString().slice(0, 10);
  let todayTasks = [];
  let journeyDone = 0, journeyTotal = 0;
  if (journeyActive) {
    program.weeks.forEach((w) => w.days.forEach((d) => {
      d.tasks.forEach((tk) => { journeyTotal += 1; if (tk.done) journeyDone += 1; });
      if (d.date === todayISO) todayTasks = d.tasks;
    }));
  }
  const journeyPct = journeyTotal ? Math.round((journeyDone / journeyTotal) * 100) : 0;

  const todayLogged = (stats?.logs || []).some((l) => l.date === todayISO);
  const pendingToday = journeyActive ? todayTasks.filter((tk) => !tk.done).length : 0;
  const showReminder = !reminderDismissed && stats && (journeyActive ? pendingToday > 0 : !todayLogged);
  const dismissReminder = () => {
    localStorage.setItem("rise_reminder_day", todayISO);
    setReminderDismissed(true);
  };

  return (
    <div className="App min-h-screen">
      <Nav />
      <div className="max-w-5xl mx-auto px-6 py-10">
        {/* 1. Massima del giorno (in alto a tutto) */}
        <RiseMoment />

        <div className="mb-8">
          {journeyActive ? (
            <>
              <p className="text-sm uppercase tracking-widest text-[#B5651D] font-semibold">{t("journey_home_kicker")}</p>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#3F6E85]" data-testid="journey-home-title">
                {t("day_of_journey")} {program.day} · {user?.name}
              </h1>
            </>
          ) : (
            <>
              <p className="text-sm uppercase tracking-widest text-[#B5651D] font-semibold">{t("welcome")}, {user?.name}</p>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-[#3F6E85]">
                {t("dashboard")}
              </h1>
            </>
          )}
        </div>

        {!user?.age && (
          <button onClick={() => navigate("/profile")} data-testid="home-age-prompt"
            className="w-full text-left rounded-2xl p-4 mb-8 bg-[#FBEEDF] border border-[#f0d3ad] text-sm text-[#7a5a2a] hover:bg-[#f7e6cf] transition-colors">
            {t("profile_age_alert")}
          </button>
        )}

        {showReminder && (
          <div className="rounded-2xl p-4 mb-8 bg-[#FBEEDF] border border-[#f0d3ad] flex items-center gap-3" data-testid="daily-reminder">
            <span className="w-9 h-9 rounded-full bg-[#E08A3C]/20 flex items-center justify-center shrink-0">
              <Bell className="w-4 h-4 text-[#B5651D]" />
            </span>
            <p className="text-sm text-[#7a5a2a] flex-1">
              {journeyActive ? `${t("reminder_journey_a")} ${pendingToday} ${t("reminder_journey_b")}` : t("reminder_analysis")}
            </p>
            <Button size="sm" onClick={() => {
              if (journeyActive) {
                const pend = todayTasks.find((tk) => !tk.done) || todayTasks[0];
                navigate(`/learn/${pend?.pillar || "exercise"}`);
              } else {
                navigate("/track");
              }
            }} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white shrink-0" data-testid="reminder-cta">
              {journeyActive ? t("reminder_cta_journey") : t("reminder_cta_analysis")}
            </Button>
            <button onClick={dismissReminder} data-testid="reminder-dismiss" className="text-[#a8834a] hover:text-[#7a5a2a] shrink-0" aria-label={t("reminder_dismiss")}>
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* 1. Dashboard a Cerchi (attività giornaliera per pilastro) */}
        <ActivityRings />

        {/* 4. Andamento Età Biologica */}
        <BioAge />

        {/* 3. Blocco Viaggio della Longevità */}
        {journeyActive && (
          <div className="rounded-3xl p-8 bg-white border border-stone-200 mb-8" data-testid="today-tasks">
            <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="font-serif text-2xl tracking-tight text-[#3F6E85]">{t("today_tasks")}</h2>
                <p className="text-sm text-[#6E6B65]">{t("journey_progress")}: {journeyDone}/{journeyTotal} · {journeyPct}%</p>
              </div>
              <Button variant="outline" onClick={() => navigate("/program")} className="rounded-full gap-2" data-testid="open-full-program">
                {t("open_full_program")} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="mb-6"><Progress value={journeyPct} className="h-2" /></div>
            {todayTasks.filter((tk) => tk.workout).length > 0 ? (
              <div className="space-y-4" data-testid="today-workouts">
                {todayTasks.filter((tk) => tk.workout).map((tk) => (
                  <div key={tk.id} data-testid={`today-workout-${tk.id}`}>
                    <p className="font-medium text-[#1A1D20] mb-1 flex items-center gap-2">
                      <Dumbbell className="w-4 h-4 text-[#C81D6B]" /> {tk.title}
                    </p>
                    <WorkoutCard workout={tk.workout} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[#6E6B65]" data-testid="no-workout-today">{t("no_workout_today")}</p>
            )}
          </div>
        )}

        {!journeyActive && analysisComplete && (
          <div className="rounded-3xl p-8 bg-[#3F6E85] text-white mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="generate-journey-cta">
            <div>
              <h3 className="font-serif text-2xl tracking-tight mb-1">{t("journey")}</h3>
              <p className="text-white/80 text-sm max-w-md">
                {lang === "it" ? "La tua settimana di analisi è completa: genera il tuo Viaggio della Longevità di 12 settimane." : "Your analysis week is complete: generate your 12-week Longevity Journey."}
              </p>
            </div>
            <Button onClick={() => navigate("/program")} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white shrink-0" data-testid="go-generate-program">
              {t("generate_program")}
            </Button>
          </div>
        )}

        {/* 6. Obiettivi della settimana (cliccabili) */}
        {goals.length > 0 && (
          <div className="rounded-3xl p-8 bg-white border border-stone-200 mb-8" data-testid="weekly-goals">
            <div className="flex items-center gap-2 mb-6 text-[#3F6E85]">
              <Target className="w-5 h-5" />
              <h2 className="font-serif text-2xl tracking-tight">{t("weekly_goals_title")}</h2>
            </div>
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-1">
              {goals.map((g) => {
                const meta = AREA_META[g.pillar] || {};
                const Icon = meta.icon;
                const expanded = openGoal === g.pillar;
                return (
                  <div key={g.pillar} data-testid={`goal-${g.pillar}`} className="border-b border-stone-100 last:border-0">
                    <button onClick={() => setOpenGoal(expanded ? null : g.pillar)} data-testid={`goal-toggle-${g.pillar}`} className="w-full text-left py-2.5 flex items-center justify-between gap-3 group">
                      <span className="flex items-center gap-2 text-sm font-medium text-[#2B4A59] group-hover:text-[#3F6E85]">
                        {Icon && <Icon className="w-4 h-4" style={{ color: meta.color }} />}
                        <span className="group-hover:underline decoration-[#3F6E85]/40 underline-offset-4">{t(g.pillar)}</span>
                        {g.is_focus && <span className="text-[10px] uppercase tracking-wider text-[#B5651D] font-semibold">focus</span>}
                      </span>
                      <span className="flex items-center gap-1.5 text-xs text-[#6E6B65]">
                        {g.current}/{g.target}
                        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${expanded ? "rotate-180" : ""}`} />
                      </span>
                    </button>
                    {expanded && (
                      <div className="mb-2.5 rounded-xl bg-[#F4F6FA] border border-[#3F6E85]/15 p-3 flex gap-2" data-testid={`goal-action-${g.pillar}`}>
                        <Target className="w-4 h-4 text-[#3F6E85] shrink-0 mt-0.5" />
                        <div>
                          <p className="text-[10px] uppercase tracking-wider text-[#3F6E85] font-semibold mb-0.5">{t("goal_today_label")}</p>
                          <p className="text-sm text-[#3a3a3a]">{g.action}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ANALYSIS MODE: track + report */}
        {!journeyActive && (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-serif text-2xl tracking-tight">{t("track_today")}</h2>
              <Button data-testid="track-today-btn" onClick={() => navigate("/track")} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59] text-white gap-2">
                {t("track_today")} <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="mb-8"><ReportSection /></div>
          </>
        )}

        {/* Strumenti secondari (compatti, in fondo) */}
        <p className="text-xs uppercase tracking-widest text-[#6E6B65] font-semibold mb-3 mt-4">{t("home_tools")}</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="home-tools">
          <button onClick={() => navigate("/diary")} data-testid="open-report-card"
            className="group flex items-center gap-3 rounded-2xl px-4 py-3.5 bg-gradient-to-br from-[#2B4A59] to-[#3F6E85] text-white hover:-translate-y-0.5 transition-all text-left shadow-[0_4px_16px_rgba(63,110,133,0.18)]">
            <Gem className="w-5 h-5 text-[#F0B27A] shrink-0 group-hover:scale-110 transition-transform" />
            <span className="text-sm font-semibold truncate">{t("longevity_diary")}</span>
          </button>
          <button onClick={() => navigate("/track")} data-testid="open-daily-card"
            className="flex items-center gap-3 rounded-2xl px-4 py-3.5 bg-white border border-stone-200 hover:border-[#E08A3C]/50 hover:-translate-y-0.5 transition-all text-left">
            <NotebookPen className="w-5 h-5 text-[#B5651D] shrink-0" />
            <span className="text-sm font-medium text-[#2B4A59] truncate">{t("daily_diary")}</span>
          </button>
          <button onClick={() => navigate("/summary")} data-testid="open-summary-card"
            className="flex items-center gap-3 rounded-2xl px-4 py-3.5 bg-white border border-stone-200 hover:border-[#3F6E85]/40 hover:-translate-y-0.5 transition-all text-left">
            <BarChart3 className="w-5 h-5 text-[#3F6E85] shrink-0" />
            <span className="text-sm font-medium text-[#2B4A59] truncate">{t("summary")}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportSection() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      await api.post(`/report/generate?lang=${lang}`);
      toast.info(lang === "it" ? "Report in generazione..." : "Generating report...");
      navigate("/reports");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-3xl p-8 bg-[#F3F1ED] border border-stone-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
      <div>
        <h3 className="font-serif text-2xl tracking-tight mb-1">{t("weekly_report")}</h3>
        <p className="text-[#6E6B65] text-sm max-w-md">
          {lang === "it" ? "Analizza i tuoi 7 giorni e ricevi un piano personalizzato guidato dall'AI." : "Analyze your 7 days and get an AI-guided personalized plan."}
        </p>
      </div>
      <div className="flex gap-3">
        <Button data-testid="generate-report-btn" onClick={generate} disabled={busy} className="rounded-full bg-[#3F6E85] hover:bg-[#2B4A59]">
          {busy ? t("generating") : t("generate_report")}
        </Button>
        <Button variant="outline" onClick={() => navigate("/reports")} className="rounded-full" data-testid="view-reports-btn">
          {t("reports")}
        </Button>
      </div>
    </div>
  );
}
